import type { MantineSize } from "@mantine/core";
import { MENU_ITEM_PADDING } from "../menu/menu.constants";
import { OVERLAY_AVAILABLE_HEIGHT } from "../popover";

/** The search input's placeholder when the consumer gives none: Explore's filter menu copy. */
export const DEFAULT_SEARCH_PLACEHOLDER = "Search filter";

/** The text size of a row's label. */
export const ROW_LABEL_SIZE: MantineSize = "sm";

/** The padding above, and again below, a row's label: the menu item's own. */
const ROW_PADDING = `var(--mantine-spacing-${MENU_ITEM_PADDING})`;

/** The height of a row label's line: its font size times its line height. */
const ROW_LABEL_LINE_HEIGHT = `calc(var(--mantine-font-size-${ROW_LABEL_SIZE}) * var(--mantine-line-height-${ROW_LABEL_SIZE}))`;

/** A row's height: its label's line, with the padding above and below it. */
const ROW_HEIGHT = `calc(2 * ${ROW_PADDING} + ${ROW_LABEL_LINE_HEIGHT})`;

/** How many rows a searchable menu's list, and each of its sub-menus, shows before it scrolls. */
export const SEARCHABLE_MENU_VISIBLE_ROWS = 11;

/**
 * The height of a number of rows.
 * @param rows How many rows.
 * @returns A CSS length.
 */
export const getRowsHeight = (rows: number) => `calc(${rows} * ${ROW_HEIGHT})`;

/**
 * `maxMenuHeight`'s default for a `searchable` AutoMenu: `SEARCHABLE_MENU_VISIBLE_ROWS` rows. On a short window the
 * list gives up height so that the whole menu fits the room beside its target (see `AutoMenu`).
 */
export const SEARCHABLE_MENU_MAX_HEIGHT = getRowsHeight(SEARCHABLE_MENU_VISIBLE_ROWS);

/**
 * `maxSubMenuHeight`'s default for a `searchable` AutoMenu: as many rows as the list, or the room available when that's
 * less. A category can be long (one row per tag group, say), and would otherwise reach `Menu.Sub.Dropdown`'s default
 * cap. The sub-menus sit inside the menu's dropdown, so they read the room it publishes.
 */
export const SEARCHABLE_SUB_MENU_MAX_HEIGHT = `min(${SEARCHABLE_MENU_MAX_HEIGHT}, var(${OVERLAY_AVAILABLE_HEIGHT}, 60vh))`;

/**
 * The room the menu has beside its target, which `Menu` publishes on its dropdown. A capped menu fits its content in
 * it, the list giving up height first, since the header and search above it don't shrink.
 */
export const MENU_AVAILABLE_HEIGHT = `var(${OVERLAY_AVAILABLE_HEIGHT})`;

/** A scroll area's scrollbar, track or thumb. */
export const SCROLLBAR_SELECTOR = ".mantine-ScrollArea-scrollbar";

/** A menu's dropdown or one of its sub-menus' dropdowns, as Mantine marks them. */
export const MENU_DROPDOWN_SELECTOR = "[data-menu-dropdown]";
